import { cmxTypeguards } from '@codametrix/ui-common';
import { AnyAction } from '@reduxjs/toolkit';
import { Store } from 'redux';

export const isSubscriptionTransformer = (
  subscription: CMx.ChannelSubscription
): subscription is CMx.SubscriptionTransformer => {
  return 'match' in subscription;
};

export const isSubscriptionObserver = (
  subscription: CMx.ChannelSubscription
): subscription is CMx.SubscriptionObserver => {
  return 'observe' in subscription;
};

export const passthrough = (action: AnyAction) => action;

export const receiverFactory = (channel: BroadcastChannel | WindowProxy) => {
  return (subscriptions: CMx.ChannelSubscription[], store: Store) => {
    const transformers = subscriptions.filter(isSubscriptionTransformer);
    const observers = subscriptions.filter(isSubscriptionObserver);

    transformers.forEach(receiver => {
      receiver.transform = receiver.transform ?? passthrough;
    });

    // For WindowProxy (window.postMessage), validate message origin to prevent
    // cross-origin attacks. BroadcastChannel is same-origin by design.
    const isWindowProxy = 'location' in channel && 'postMessage' in channel;
    const expectedOrigin = isWindowProxy
      ? (channel as Window).location.origin
      : null;

    channel.addEventListener('message', (message: MessageEvent | Event) => {
      // Validate origin for WindowProxy messages
      if (
        expectedOrigin &&
        'origin' in message &&
        (message as MessageEvent).origin !== expectedOrigin
      ) {
        return;
      }

      // subscriptions deal with actions instead of MessageEvents
      // if a message comes across the channel that isn't an action
      // it is ignored for now.
      if ('data' in message && cmxTypeguards.isReduxAction(message.data)) {
        const action = message.data;
        // observers go first.
        observers.forEach(observer => observer.observe(action));

        const dispatchables = transformers.filter(receiver =>
          receiver.match(action)
        );

        dispatchables.forEach(dispatchable => {
          // trans guaranteed to be there from lines above.
          const toDispatch = (dispatchable.transform as CMx.ActionTransformer)({
            ...action
          });
          store.dispatch(toDispatch);
        });
      }
    });
  };
};
